/**
 * sheetsClient — read the daily "Prelim Anes Assignments" Google Sheet from the
 * browser using Google Identity Services (GIS) OAuth + the Sheets/Drive REST API.
 *
 * No gapi/googleapis bundle: we load the tiny GIS script once, get a read-only
 * access token via the implicit token-client popup, and fetch directly.
 * Operational schedule data only (no PHI), read-only scopes.
 *
 * DAILY-FILE LOOKUP: the anesthesia department publishes a NEW file each day,
 * titled "Prelim Anes Assignments M.D.YY" — so the spreadsheet id changes daily
 * and cannot be hardcoded. At import time we query the Drive API for the most
 * recently modified file whose title starts with the configured prefix, and read
 * that. A fixed REACT_APP_ATC_SHEET_ID, if set, overrides the lookup (test/dev).
 *
 * CONFIG (set in .env.local — the client id is public and safe in the bundle):
 *   REACT_APP_GOOGLE_CLIENT_ID   OAuth 2.0 Web client id (Sheets API + Drive API
 *                                enabled, authorized JS origins: localhost:3000
 *                                + prod). Adding the Drive scope below needs the
 *                                Drive API enabled and a one-time re-consent.
 *   REACT_APP_ATC_SHEET_TITLE    optional title prefix; default below
 *   REACT_APP_ATC_SHEET_ID       optional FIXED spreadsheet id; when set, skips
 *                                the daily Drive lookup and reads this id
 *   REACT_APP_ATC_SHEET_RANGE    optional A1 range; default below
 */

import { tabRange, scoreTabForAssignments, pickAssignmentsTab } from "./pickTab";
import { QGENDA_TAB_TITLE } from "./qgendaIdentity";

// Re-export the pure tab-picking helpers so existing importers/tests that pull
// them from sheetsClient keep working; the implementation lives in ./pickTab so
// the scheduled Cloud Function can share it without the browser client.
export { tabRange, scoreTabForAssignments, pickAssignmentsTab };

const GIS_SRC = "https://accounts.google.com/gsi/client";
const SHEETS_SCOPE = "https://www.googleapis.com/auth/spreadsheets.readonly";
// drive.metadata.readonly is enough for files.list (title search) — it does NOT
// grant read of file CONTENT (the Sheets scope does that), so this stays minimal.
const DRIVE_SCOPE = "https://www.googleapis.com/auth/drive.metadata.readonly";
const SCOPES = `${SHEETS_SCOPE} ${DRIVE_SCOPE}`;
const SPREADSHEET_MIME = "application/vnd.google-apps.spreadsheet";
export const DEFAULT_RANGE = "A:Z";
export const DEFAULT_SHEET_TITLE = "Prelim Anes Assignments";

export const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
// Title prefix of the daily file. The dept appends a date ("... 6.22.26"); we
// match by prefix and take the newest, so the date format can drift safely.
export const ATC_SHEET_TITLE = process.env.REACT_APP_ATC_SHEET_TITLE || DEFAULT_SHEET_TITLE;
// Optional fixed id — when present, the daily Drive lookup is skipped. Empty by
// default so the lookup runs; set it to pin a specific test/dev spreadsheet.
export const ATC_SHEET_ID = process.env.REACT_APP_ATC_SHEET_ID || "";
export const ATC_SHEET_RANGE = process.env.REACT_APP_ATC_SHEET_RANGE || DEFAULT_RANGE;
// Optional EXACT tab/sheet title to read (e.g. "Assignment Sheet"). When set, we
// read only that tab and skip the multi-tab scan. Empty by default so the scan
// auto-detects the filled assignments tab.
export const ATC_SHEET_TAB = process.env.REACT_APP_ATC_SHEET_TAB || "";

// A1 range that already names a tab ("'Sheet'!A:Z") — read as-is, no scan.
const rangeNamesTab = (range) => /!/.test(String(range || ""));

/** Build the Sheets values.get URL. Exported for testing (pure). */
export function valuesUrl(spreadsheetId, range = DEFAULT_RANGE) {
  if (!spreadsheetId) throw new Error("Missing spreadsheetId.");
  return (
    `https://sheets.googleapis.com/v4/spreadsheets/${encodeURIComponent(spreadsheetId)}` +
    `/values/${encodeURIComponent(range)}?majorDimension=ROWS`
  );
}

/**
 * Build the Drive files.list URL that finds spreadsheets whose title starts with
 * `titlePrefix`, newest first. Exported for testing (pure).
 *
 * Drive's query language has no "starts-with", so we use `name contains` (a
 * superset) and then verify the prefix client-side in pickLatestFile.
 */
export function driveSearchUrl(titlePrefix = ATC_SHEET_TITLE) {
  const prefix = String(titlePrefix || "").trim();
  if (!prefix) throw new Error("Missing sheet title — set REACT_APP_ATC_SHEET_TITLE.");
  const q =
    `name contains '${prefix.replace(/'/g, "\\'")}' ` +
    `and mimeType = '${SPREADSHEET_MIME}' and trashed = false`;
  const params = new URLSearchParams({
    q,
    orderBy: "modifiedTime desc",
    pageSize: "10",
    fields: "files(id,name,modifiedTime)",
    // Include files shared with the user (the dept owns + shares the daily file).
    includeItemsFromAllDrives: "true",
    supportsAllDrives: "true",
    corpora: "allDrives",
  });
  return `https://www.googleapis.com/drive/v3/files?${params.toString()}`;
}

/**
 * From a Drive files.list payload, pick the newest file whose title actually
 * starts with `titlePrefix` (case-insensitive) — `name contains` can return
 * mid-string matches we don't want. Files come pre-sorted newest-first.
 * Pure; exported for testing.
 *
 * @returns {{id, name, modifiedTime}|null}
 */
export function pickLatestFile(payload, titlePrefix = ATC_SHEET_TITLE) {
  const files = Array.isArray(payload?.files) ? payload.files : [];
  const pfx = String(titlePrefix || "").trim().toLowerCase();
  return (
    files.find((f) => String(f?.name || "").trim().toLowerCase().startsWith(pfx)) || null
  );
}

/** Inject the GIS client script once; resolve when google.accounts is ready. */
export function loadGis() {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("No window — browser only."));
    if (window.google?.accounts?.oauth2) return resolve(window.google);
    const existing = document.querySelector(`script[src="${GIS_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.google), { once: true });
      existing.addEventListener("error", () => reject(new Error("Failed to load Google Identity script.")), { once: true });
      return undefined;
    }
    const el = document.createElement("script");
    el.src = GIS_SRC;
    el.async = true;
    el.defer = true;
    el.onload = () => resolve(window.google);
    el.onerror = () => reject(new Error("Failed to load Google Identity script."));
    document.head.appendChild(el);
    return undefined;
  });
}

/**
 * Run the OAuth token popup and resolve with a read-only access token.
 * @param {string} clientId  OAuth Web client id
 * @returns {Promise<string>} access token
 */
export async function requestAccessToken(clientId = GOOGLE_CLIENT_ID) {
  if (!clientId) throw new Error("Missing Google client id — set REACT_APP_GOOGLE_CLIENT_ID.");
  const google = await loadGis();
  return new Promise((resolve, reject) => {
    const tokenClient = google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: SCOPES,
      callback: (resp) => {
        if (resp?.error) return reject(new Error(`Google auth failed: ${resp.error}`));
        if (!resp?.access_token) return reject(new Error("No access token returned."));
        return resolve(resp.access_token);
      },
      error_callback: (err) => reject(new Error(err?.message || "Google auth was cancelled.")),
    });
    tokenClient.requestAccessToken();
  });
}

/**
 * Resolve the spreadsheet id of today's "Prelim Anes Assignments" file via the
 * Drive API: search by title prefix, return the newest match. Throws a clear
 * error when nothing matches (e.g. the day's file isn't published yet).
 *
 * @returns {Promise<{id, name, modifiedTime}>}
 */
export async function findLatestSheetId({ titlePrefix = ATC_SHEET_TITLE, token } = {}) {
  if (!token) throw new Error("Missing access token.");
  const res = await fetch(driveSearchUrl(titlePrefix), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(`Drive API ${res.status}: ${detail.slice(0, 200)}`);
  }
  const json = await res.json();
  const file = pickLatestFile(json, titlePrefix);
  if (!file) {
    throw new Error(`No Google Sheet found titled "${titlePrefix} …" — is today's file shared yet?`);
  }
  return file;
}

/**
 * Fetch a values range with a bearer token. Returns the raw 2D `values` array
 * (ragged rows possible), ready for parsePrelimAssignments.
 */
export async function readSheetValues({ spreadsheetId, range = ATC_SHEET_RANGE, token }) {
  if (!token) throw new Error("Missing access token.");
  const res = await fetch(valuesUrl(spreadsheetId, range), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(`Sheets API ${res.status}: ${detail.slice(0, 200)}`);
  }
  const json = await res.json();
  return Array.isArray(json.values) ? json.values : [];
}

/** List the tab/sheet titles in a spreadsheet, in sheet order. */
export async function listSheetTitles({ spreadsheetId, token }) {
  if (!token) throw new Error("Missing access token.");
  const url =
    `https://sheets.googleapis.com/v4/spreadsheets/${encodeURIComponent(spreadsheetId)}` +
    `?fields=${encodeURIComponent("sheets.properties(title,index)")}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(`Sheets API ${res.status}: ${detail.slice(0, 200)}`);
  }
  const json = await res.json();
  return (Array.isArray(json.sheets) ? json.sheets : [])
    .map((s) => s?.properties?.title)
    .filter((t) => typeof t === "string" && t.length > 0);
}

/**
 * Read the same A1 range from every named tab in ONE batchGet. Returns
 * [{title, values}] aligned to `titles`.
 */
export async function readTabsValues({ spreadsheetId, titles = [], range = DEFAULT_RANGE, token }) {
  if (!token) throw new Error("Missing access token.");
  if (!titles.length) return [];
  const params = new URLSearchParams({ majorDimension: "ROWS" });
  titles.forEach((t) => params.append("ranges", tabRange(t, range)));
  const url =
    `https://sheets.googleapis.com/v4/spreadsheets/${encodeURIComponent(spreadsheetId)}` +
    `/values:batchGet?${params.toString()}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(`Sheets API ${res.status}: ${detail.slice(0, 200)}`);
  }
  const json = await res.json();
  const ranges = Array.isArray(json.valueRanges) ? json.valueRanges : [];
  return titles.map((title, i) => ({
    title,
    values: Array.isArray(ranges[i]?.values) ? ranges[i].values : [],
  }));
}

/**
 * Read the ATTENDING column's cell FORMATTING (bold + text colour) for a tab.
 * The sheet marks Tampa's DOD (their AIC) by bold/colour alone — invisible to
 * values.get — so this one extra fields-limited call carries the signal.
 * Returns an array indexed by sheet row (0-based from row 1):
 *   [{ bold, colorKey }] — colorKey = "r,g,b" rounded, "" when unset.
 * Soft-fails to null (formatting is an enhancement, never a blocker).
 */
export async function readAttendingFormats({ spreadsheetId, tab, token, column = "E" }) {
  try {
    const rangeRef = tab ? `${tabRange(tab, `${column}:${column}`)}` : `${column}:${column}`;
    const url =
      `https://sheets.googleapis.com/v4/spreadsheets/${encodeURIComponent(spreadsheetId)}` +
      `?ranges=${encodeURIComponent(rangeRef)}` +
      `&fields=${encodeURIComponent("sheets.data.rowData.values(effectiveFormat.textFormat(bold,foregroundColor))")}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return null;
    const json = await res.json();
    const rowData = json?.sheets?.[0]?.data?.[0]?.rowData || [];
    return rowData.map((r) => {
      const tf = r?.values?.[0]?.effectiveFormat?.textFormat || {};
      const c = tf.foregroundColor || {};
      const key = [c.red, c.green, c.blue]
        .map((v) => Math.round((v || 0) * 255))
        .join(",");
      return { bold: Boolean(tf.bold), colorKey: key === "0,0,0" ? "" : key };
    });
  } catch {
    return null;
  }
}

/**
 * One-shot convenience: authenticate, resolve today's sheet (Drive title lookup,
 * unless a fixed id is configured), then read the FILLED Wesley Chapel tab.
 *
 * The daily file is a multi-tab workbook: the first tab is usually a blank
 * template ("Qgenda Report") and the real data lives on a later tab
 * ("Assignment Sheet"). A bare "A:Z" range reads only the FIRST tab, so we scan
 * every tab and pick the one that actually parses to Wesley Chapel rows. An
 * explicit tab (REACT_APP_ATC_SHEET_TAB) or a tab-qualified range short-circuits
 * the scan.
 *
 * @returns {Promise<{values: Array<Array<string>>, file: {id, name}|null, tab: string|null}>}
 */
export async function importSheetValues({
  clientId = GOOGLE_CLIENT_ID,
  spreadsheetId = ATC_SHEET_ID,
  titlePrefix = ATC_SHEET_TITLE,
  range = ATC_SHEET_RANGE,
  tab = ATC_SHEET_TAB,
} = {}) {
  const token = await requestAccessToken(clientId);
  // A fixed id (test/dev) bypasses the daily lookup; otherwise resolve by title.
  let file = null;
  let id = spreadsheetId;
  if (!id) {
    file = await findLatestSheetId({ titlePrefix, token });
    id = file.id;
  }

  /**
   * Read the workbook's OWN identity map alongside the assignments.
   *
   * ⚠️ THE TAB MUST BE NAMED. This workbook has NINE tabs and a bare "A:Z"
   * range resolves to the FIRST one — a blank template. Reading the assignments
   * that way is the bug that once made the importer report "no rooms"; reading
   * IDENTITY that way would be worse, because an empty map is not an error, it
   * silently drops the rung that turns "Chip Compton" into Kang Compton.
   *
   * ⚠️ SOFT-FAILS TO NULL. The tab is hand-edited too, and can be renamed or
   * removed. The ladder degrades to nickname + prefix and the review queue gets
   * larger — which is visible in the coverage line. An import that refused to
   * run because a sidecar tab moved would be a worse failure.
   */
  const readQgenda = async (tabsAlreadyRead) => {
    const cached = (tabsAlreadyRead || []).find((t) => t.title === QGENDA_TAB_TITLE);
    if (cached) return cached.values;
    try {
      return await readSheetValues({
        spreadsheetId: id, range: tabRange(QGENDA_TAB_TITLE, DEFAULT_RANGE), token,
      });
    } catch {
      return null;
    }
  };

  // Explicit tab name, or a range that already names a tab → read exactly that.
  const finish = async (values, tabTitle, tabsAlreadyRead = null) => ({
    values,
    file,
    tab: tabTitle,
    // Attending-column formatting (bold/colour = Tampa DOD marker). Optional.
    attendingFormats: await readAttendingFormats({ spreadsheetId: id, tab: tabTitle, token }),
    // `Qgenda Report` — the department's own display-name → legal-name map.
    qgendaValues: await readQgenda(tabsAlreadyRead),
  });
  if (tab) {
    const values = await readSheetValues({ spreadsheetId: id, range: tabRange(tab, range), token });
    return finish(values, tab);
  }
  if (rangeNamesTab(range)) {
    const values = await readSheetValues({ spreadsheetId: id, range, token });
    return finish(values, null);
  }

  // Auto-detect the filled assignments tab across the workbook.
  const titles = await listSheetTitles({ spreadsheetId: id, token });
  if (titles.length <= 1) {
    const values = await readSheetValues({ spreadsheetId: id, range, token });
    return finish(values, titles[0] || null);
  }
  const tabs = await readTabsValues({ spreadsheetId: id, titles, range, token });
  const chosen = pickAssignmentsTab(tabs);
  if (chosen) return finish(chosen.values, chosen.title, tabs);
  // No tab parsed to WC rows — return the first tab so the caller's diagnostic
  // still reports what was actually read.
  return finish(tabs[0]?.values || [], tabs[0]?.title || null, tabs);
}
